// @ts-strict-ignore
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
// eslint-disable-next-line import/no-namespace
import * as SS from 'styled-system';
import {
  BorderProps,
  ColorProps,
  DisplayProps,
  LayoutProps,
  MarginProps,
  OverflowProps,
  PaddingProps,
  TextAlignProps,
  TypographyProps,
} from 'styled-system';

import { colors, getClassName, textStyles as styles } from 'theme';
import { AmpContext } from 'utils/AmpContext';
import { setupLinkTracker } from 'utils/analytics';
import { IDEALIST_UTM_PARAMS } from 'utils/constants/general';
import { hasUTMParam, isExternalURL, pathWithParams } from 'utils/url';

type StyleProps = MarginProps &
  ColorProps &
  Omit<TypographyProps, 'fontWeight'> &
  PaddingProps &
  BorderProps &
  OverflowProps &
  DisplayProps &
  LayoutProps & {
    fontWeight?: TypographyProps['fontWeight'] | 'semiBold' | '100' | '600';
    whiteSpace?: string;
    textOverflow?: string;
    textDecoration?: string;
    styleAs?: string;
    textTransform?: string;
    $lineClamp?: number;
  };

type AnalyticsData =
  | {
      eventName: string;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties: Record<string, any>;
    } // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | Record<string, any>;

const styledSystem = css`
  ${SS.space}
  ${SS.overflow}
  ${SS.color}
  ${SS.opacity}
  ${SS.fontFamily}
  ${SS.fontSize}
  ${SS.fontWeight}
  ${SS.fontStyle}
  ${SS.letterSpacing}
  ${SS.textAlign}
  ${SS.lineHeight}
  ${SS.borders}
  ${SS.borderColor}
  ${SS.display}
  ${SS.verticalAlign}
  ${SS.shadow}
`;

const Text = styled(
  ({
    tag = 'div',
    styleAs,
    analyticsData,
    ...props
  }: {
    tag?: string;
    styleAs?: string;
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    className?: string;
    title?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  }) => {
    const ref = useRef<Element | null | undefined>();
    const [linksAreTracked, setLinksAreTracked] = useState(false);
    const [linksHaveUTMParams, setLinksHaveUTMParams] = useState(false);
    useEffect(() => {
      if (!linksHaveUTMParams) {
        if (ref && ref.current) {
          const linkEls = [...ref.current.getElementsByTagName('a')];
          linkEls.forEach((link) => {
            const href = link.getAttribute('href') || '';

            if (isExternalURL(href) && !hasUTMParam(href)) {
              link.setAttribute(
                'href',
                pathWithParams(href, IDEALIST_UTM_PARAMS)
              );
            }
          });
        }

        setLinksHaveUTMParams(true);
      }
    }, [linksHaveUTMParams, setLinksHaveUTMParams]);
    useEffect(() => {
      const { eventName, eventProperties } = analyticsData || {};

      if (eventProperties && eventName && !linksAreTracked) {
        if (ref && ref.current) {
          const linkEls = [...ref.current.getElementsByTagName('a')];
          linkEls.forEach((link) => {
            if (
              link.hasAttribute('href') &&
              link.getAttribute('href') !== '#'
            ) {
              const href = link.getAttribute('href') || '';
              setupLinkTracker(eventName, {
                ...eventProperties,
                url: href.split('?')[0],
              })(link);
            }
          });
        }

        setLinksAreTracked(true);
      }
    }, [analyticsData, linksAreTracked, setLinksAreTracked]);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const otherProps: Record<string, any> = Object.keys(props).reduce(
      (acc, key) =>
        [
          'm',
          'mt',
          'mb',
          'ml',
          'mr',
          'mx',
          'my',
          'p',
          'pt',
          'pb',
          'pl',
          'pr',
          'px',
          'py',
          'overflow',
          'color',
          'fontFamily',
          'fontSize',
          'fontWeight',
          'letterSpacing',
          'textAlign',
          'textTransform',
          'textOverflow',
          'textDecoration',
          'whiteSpace',
          'lineHeight',
          'border',
          'borderLeft',
          'borderRight',
          'borderTop',
          'borderBottom',
          'borderColor',
          'opacity',
          'display',
          'verticalAlign',
          'textShadow',
          'styleAs',
        ].includes(key)
          ? acc
          : { ...acc, [key]: props[key] },
      {}
    );
    const Tag = tag;
    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ ref: MutableRefObject<Element>; }' is not ... Remove this comment to see the full error message
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Tag ref={ref} {...otherProps} />;
  }
).attrs(
  ({
    // @ts-expect-error Fix types
    textOverflow,
    // @ts-expect-error Fix types
    textTransform,
    // @ts-expect-error Fix types
    textDecoration,
    // @ts-expect-error Fix types
    whiteSpace,
    // @ts-expect-error Fix types
    textShadow,
  }) => ({
    style: {
      textOverflow,
      textTransform,
      textDecoration,
      whiteSpace,
      textShadow,
    },
  })
)<StyleProps>`
  ${(props) =>
    props.styleAs &&
    styles[props.styleAs] &&
    `
    ${styles[props.styleAs]}
  `}
  ${
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) =>
      props.$lineClamp &&
      `
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-line-clamp: ${props.$lineClamp};
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    overflow: hidden;
`
  }
  ${styledSystem}
`;
// eslint-disable-next-line import/no-default-export
export default Text;
const StyledRichText = styled(
  ({
    tag = 'div',
    simple,
    small,
    normalize,
    html,
    requestIsAmp,
    className,
    ...props
  }: {
    tag?: string;
    simple?: boolean;
    small?: boolean;
    normalize?: boolean;
    html?: string;
    requestIsAmp?: boolean;
    className?: string;
  }) => {
    const cleanHtml = html
      ? html.replace(
          /<(p|h[1-6]|blockquote)>(<br\/?>)?<\/(p|h[1-6]|blockquote)>/g,
          ''
        )
      : '';
    return (
      <Text
        tag={tag}
        dangerouslySetInnerHTML={
          html
            ? {
                __html: cleanHtml,
              }
            : undefined
        }
        className={getClassName(className, ['rich-text'])}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    );
  }
)`
  overflow-wrap: break-word;

  &.idlst-rchtxt {
    ${(props) =>
      !props.requestIsAmp &&
      `
  ${
    props.simple && !props.requestIsAmp
      ? `
    h1, h2, h3, h4, h5 {
      margin: 1em 0;
      opacity: 0.8;
      font-size: inherit !important;
    }
  `
      : ''
  }
  ${
    props.small &&
    `
    ${styles.bodySmall}

    h1, h2, h3, h4 {
      margin: 1em 0;
      ${styles.h4}
    }
  `
  }
  ${
    props.normalize && !props.requestIsAmp
      ? `
    h1, h2, h3 {
      ${styles.h3}
    }

    h4, h5 {
      font-size: inherit !important;
    }

    h1, h2, h3, h4, h5 {
      margin: 1em 0;
      opacity: 0.8;
    }
  `
      : ''
  }`}
  }
`;
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition, @typescript-eslint/no-explicit-any
export const RichText = (props: Record<string, any>) => (
  <AmpContext.Consumer>
    {(requestIsAmp) => (
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/jsx-props-no-spreading
      <StyledRichText requestIsAmp={requestIsAmp} {...props} />
    )}
  </AmpContext.Consumer>
);
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const H1 = (
  {
    tag = 'h1',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="h1" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const H2 = (
  {
    tag = 'h2',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="h2" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const H3 = (
  {
    tag = 'h3',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="h3" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const H4 = (
  {
    tag = 'h4',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="h4" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const H5 = (
  {
    tag = 'h5',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="h5" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const H6 = (
  {
    tag = 'h6',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="h6" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const HeadingSmall = (
  {
    tag = 'h5',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="headingSmall" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const BodyLarge = (
  {
    tag = 'div',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="bodyLarge" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const Body = (
  {
    tag = 'div',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    className?: string;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="body" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const BodySmall = (
  {
    tag = 'div',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="bodySmall" {...props} />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const BodyMicro = (
  {
    tag = 'div',
    ...props
  }: StyleProps & {
    children?: ReactNode;
    analyticsData?: AnalyticsData;
    dangerouslySetInnerHTML?: { __html: string };
    id?: string;
    tag?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: any;
  } // TODO: Fix this the next time the file is edited.
) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Text tag={tag} styleAs="bodyMicro" {...props} />
);

export const Underline = styled(
  (
    {
      tag = 'span',
      ...props
    }: StyleProps & {
      children?: ReactNode;
      analyticsData?: AnalyticsData;
      dangerouslySetInnerHTML?: { __html: string };
      id?: string;
      tag?: string;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      as?: any;
    } // TODO: Fix this the next time the file is edited.
  ) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Text tag={tag} styleAs="underline" {...props} />
  )
)`
  @keyframes aUnderline {
    from {
      background-size: 0 0.3em;
    }
    to {
      background-size: 100% 0.3em;
    }
  }
  background-repeat: no-repeat;
  background-size: 0 0.3em;
  animation: aUnderline 1s cubic-bezier(0.215, 0.61, 0.355, 1) 1s forwards;
`;
export const RichTextHeadline = styled(
  ({
    children,
    className,
    tag,
    ...props
  }: {
    children: string;
    className?: string;
    styleAs?: string;
    tag?: string;
  }) => {
    const html =
      children && children.indexOf('h2') > -1
        ? children
        : `<h2>${
            children ? children.replace(/<(\/?)p>/g, '<$1span>') : ''
          }</h2>`;
    return (
      <RichText
        className={className}
        dangerouslySetInnerHTML={{
          __html: tag ? html.replace(/<(\/?)h2>/g, `<$1${tag}>`) : html,
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    );
  }
)<
  {
    $reverseUnderline?: boolean;
    styleAs?: string;
    tag?: string;
  } & TextAlignProps &
    MarginProps &
    ColorProps
>`
  &&.idlst-rchtxt {
    ${(props) => props.tag || 'h2'} {
      margin-bottom: 1em;
      ${(props) =>
        props.$reverseUnderline
          ? `
      strong,
      u,
      b,
      em {
        background-image: linear-gradient(
          to bottom,
          ${colors.waterBlue} 0%,
          ${colors.waterBlue} 100%
        );
      }`
          : ''}
    }

    ${(props) =>
      props.styleAs &&
      styles[props.styleAs] &&
      `
      ${props.tag || 'h2'} {
        ${styles[props.styleAs]}
      }
    `}
  }
`;
